/**
* product-interactions.js
* XStamper — Product card interactions (global, all pages)
*
* 1. Add-to-cart button — AJAX + green check animation (1.8s) + open cart drawer
* 2. Wishlist toggle — localStorage persistence + UI sync across pages
* 3. Wishlist page — reads localStorage, fetches products, renders cards
*/

(function () {
  'use strict';

  var WISHLIST_KEY = 'xstamper_wishlist';

  /* One-time migration: an older collection-page script saved hearts under
     'cl-wishlist'. Merge that data into the canonical key so nobody loses
     their saved items, then remove the legacy key. */
  (function migrateLegacyWishlist() {
    try {
      var legacy = JSON.parse(localStorage.getItem('cl-wishlist'));
      if (Array.isArray(legacy) && legacy.length) {
        var current = JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
        legacy.forEach(function (handle) {
          if (typeof handle === 'string' && current.indexOf(handle) === -1) current.push(handle);
        });
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(current));
      }
      localStorage.removeItem('cl-wishlist');
    } catch (_) {}
  })();

  /* Screen-reader live-region announcer (the region lives in layout/theme.liquid: #a11y-status) */
  function announce(message) {
    var el = document.getElementById('a11y-status');
    if (!el) return;
    el.textContent = '';
    window.setTimeout(function () { el.textContent = message; }, 60);
  }
  window.themeAnnounce = announce;

  /* Shopify-style money formatter driven by the store's money_format string
     (set on window by layout/theme.liquid). Falls back to a plain $ format.
     Keeps prices correct on Markets / non-USD stores after AJAX re-renders. */
  window.themeFormatMoney = window.themeFormatMoney || function (cents) {
    var format = window.themeMoneyFormat || '${{amount}}';
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function fmt(precision, thousands, decimal) {
      if (cents == null || isNaN(cents)) return '0';
      var num = (cents / 100).toFixed(precision);
      var parts = num.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (thousands || ','));
      return dollars + (parts[1] ? (decimal || '.') + parts[1] : '');
    }
    var match = format.match(placeholder);
    var value = fmt(2, ',', '.');
    if (match) {
      switch (match[1]) {
        case 'amount_no_decimals': value = fmt(0, ',', '.'); break;
        case 'amount_with_comma_separator': value = fmt(2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': value = fmt(0, '.', ''); break;
        case 'amount_with_space_separator': value = fmt(2, ' ', '.'); break;
      }
    }
    return format.replace(placeholder, value);
  };

  /* ═══════════════════════════════════════════════════════════════════════
     WISHLIST HELPERS
  ═══════════════════════════════════════════════════════════════════════ */

  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []; }
    catch (_) { return []; }
  }

  function setWishlist(list) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); }
    catch (_) {}
  }

  function isWishlisted(handle) {
    return getWishlist().indexOf(handle) !== -1;
  }

  function toggleWishlistItem(handle) {
    var list = getWishlist();
    var idx  = list.indexOf(handle);
    if (idx === -1) {
      list.push(handle);
    } else {
      list.splice(idx, 1);
    }
    setWishlist(list);
    return idx === -1; // true = just added
  }

  /* Sync all wishlist buttons on the page to match localStorage state */
  function syncWishlistButtons() {
    document.querySelectorAll('[data-wishlist]').forEach(function (btn) {
      var handle = btn.getAttribute('data-wishlist');
      var active = isWishlisted(handle);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active.toString());
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     ADD TO CART
  ═══════════════════════════════════════════════════════════════════════ */

  function initAddToCart() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-add-to-cart]');
      if (!btn) return;

      e.preventDefault();

      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId || btn.disabled || btn.classList.contains('is-added')) return;

      btn.disabled = true;

           // The product form (PDP). Product cards have no cart form → stays null.
      var form = btn.closest('form[action*="/cart/add"]')
              || btn.closest('form')
              || document.querySelector('form[action*="/cart/add"]');

      var qtyInput = form
        ? form.querySelector('input[name="quantity"]')
        : document.querySelector('input[name="quantity"]');
      var quantity = (qtyInput && parseInt(qtyInput.value, 10) > 0)
        ? parseInt(qtyInput.value, 10)
        : 1;

      // Collect line-item properties from the form (stamp customizer injects
      // properties[_stamp_config], properties[Lines], etc.). Empty on cards.
      var properties = {};
      if (form) {
        form.querySelectorAll('[name^="properties["]').forEach(function (input) {
          var m = input.name.match(/^properties\[(.+)\]$/);
          if (m && input.value !== '') properties[m[1]] = input.value;
        });
      }

      var payload = { id: parseInt(variantId, 10), quantity: quantity };
      if (Object.keys(properties).length) payload.properties = properties;

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify(payload)
      })
      .then(function (r) {
        if (!r.ok) throw new Error('cart/add failed: ' + r.status);

        // The item IS in the cart from here on — a hiccup in the follow-up
        // cart refresh must not be reported as a failed add.
        return fetch('/cart.js')
          .then(function (cr) {
            if (!cr.ok) throw new Error('cart refresh failed: ' + cr.status);
            return cr.json();
          })
          .catch(function () { return null; });
      })
      .then(function (cart) {
        // Success animation
        btn.classList.add('is-added');
        announce('Added to cart');

        // Notify cart drawer to refresh + open (skipped if the refresh
        // fetch failed — the add itself still succeeded)
        if (cart) {
          document.dispatchEvent(new CustomEvent('cart:added', { detail: { cart: cart } }));
        }

        // Revert after 1.8s
        setTimeout(function () {
          btn.classList.remove('is-added');
          btn.disabled = false;
        }, 1800);
      })
      .catch(function (err) {
        console.error('[ATC]', err);
        btn.disabled = false;
        btn.style.backgroundColor = 'var(--color-error)';
        setTimeout(function () { btn.style.backgroundColor = ''; }, 1200);
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     WISHLIST TOGGLE (delegated — works on all product cards)
  ═══════════════════════════════════════════════════════════════════════ */

  function initWishlistToggle() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist]');
      if (!btn) return;

      e.preventDefault();
      var handle = btn.getAttribute('data-wishlist');
      if (!handle) return;

      var added = toggleWishlistItem(handle);
      btn.classList.toggle('is-active', added);
      btn.setAttribute('aria-pressed', added.toString());
      announce(added ? 'Added to wishlist' : 'Removed from wishlist');

      // Dispatch so wishlist page can react if open
      document.dispatchEvent(new CustomEvent('wishlist:changed', {
        detail: { handle: handle, added: added, list: getWishlist() }
      }));
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     WISHLIST PAGE
     Activated only when #wishlist-grid exists (page.wishlist template)
  ═══════════════════════════════════════════════════════════════════════ */

  function initWishlistPage() {
    var grid    = document.getElementById('wishlist-grid');
    var empty   = document.getElementById('wishlist-empty');
    var loading = document.getElementById('wishlist-loading');
    var counter = document.querySelector('.wishlist-page__count');
    if (!grid) return;

    function render() {
      var handles = getWishlist();

      if (loading) loading.style.display = 'none';

      if (handles.length === 0) {
        grid.style.display  = 'none';
        if (empty) empty.style.display = '';
        if (counter) counter.textContent = '0 items';
        return;
      }

      if (empty) empty.style.display = 'none';
      if (counter) counter.textContent = handles.length + (handles.length === 1 ? ' item' : ' items');

      // Fetch all products, then render
      Promise.all(handles.map(function (handle) {
        return fetch('/products/' + handle + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      }))
      .then(function (products) {
        var validProducts = products.filter(Boolean);

        // Launch mode: wishlist only shows products carrying the launch tag
        if (window.themeLaunchTag) {
          validProducts = validProducts.filter(function (p) {
            return (p.tags || []).some(function (t) {
              return String(t).trim().toLowerCase() === window.themeLaunchTag;
            });
          });
        }

        if (counter) counter.textContent = validProducts.length + (validProducts.length === 1 ? ' item' : ' items');

        if (validProducts.length === 0) {
          grid.style.display  = 'none';
          if (empty) empty.style.display = '';
          if (counter) counter.textContent = '0 items';
          return;
        }

        grid.innerHTML = validProducts.map(function (p) {
          return buildProductCard(p);
        }).join('');

        grid.style.display = '';
        syncWishlistButtons();
      });
    }

    function buildProductCard(p) {
      var image      = p.featured_image ? p.featured_image + '&width=600' : '';
      var price      = formatMoney(p.price);
      var compareAt  = p.compare_at_price && p.compare_at_price > p.price
        ? formatMoney(p.compare_at_price) : '';
      var singleVar  = p.variants && p.variants.length === 1;
      var variantId  = singleVar ? p.variants[0].id : null;
      var available  = p.available;

      var atcBtn = '';
      if (available && singleVar && variantId) {
        atcBtn =
          '<button class="product-card__add-btn" aria-label="Add ' + escAttr(p.title) + ' to cart"' +
          ' data-add-to-cart data-variant-id="' + variantId + '">' +
            '<span class="icon--default">' + ICON_CART + '</span>' +
            '<span class="icon--active">' + ICON_CHECK + '</span>' +
          '</button>';
      } else if (available) {
        atcBtn = '<a href="/products/' + esc(p.handle) + '" class="product-card__add-btn" aria-label="Select options">' + ICON_CART + '</a>';
      }

      var saleBadge = (p.compare_at_price && p.compare_at_price > p.price)
        ? '<span class="badge badge--sale">Sale</span>' : '';

      return (
        '<div class="product-card">' +
          '<div class="product-card__image-wrap">' +
            '<div class="product-card__badges">' + saleBadge + '</div>' +
            (image
              ? '<a href="/products/' + esc(p.handle) + '" tabindex="-1"><img class="product-card__image" src="' + image + '" alt="' + escAttr(p.title) + '" loading="lazy"></a>'
              : '<a href="/products/' + esc(p.handle) + '" tabindex="-1"></a>'
            ) +
            '<div class="product-card__actions">' +
              '<button class="product-card__action-btn is-active" aria-label="Add ' + escAttr(p.title) + ' to wishlist"' +
              ' aria-pressed="true" data-wishlist="' + esc(p.handle) + '">' +
                '<span class="icon--default">' + ICON_HEART + '</span>' +
                '<span class="icon--active">' + ICON_HEART_FILL + '</span>' +
              '</button>' +
            '</div>' +
          '</div>' +
          '<div class="product-card__body">' +
            (p.product_type ? '<span class="product-card__category">' + esc(p.product_type) + '</span>' : '') +
            '<a href="/products/' + esc(p.handle) + '" class="product-card__title">' + esc(p.title) + '</a>' +
            '<div class="product-card__price-row">' +
              '<div class="product-card__price">' +
                '<span class="product-card__price-current">' + price + '</span>' +
                (compareAt ? '<span class="product-card__price-original">' + compareAt + '</span>' : '') +
              '</div>' +
              atcBtn +
            '</div>' +
          '</div>' +
        '</div>'
      );
    }

    render();

    // Re-render when another tab or page modifies wishlist
    window.addEventListener('storage', function (e) {
      if (e.key === WISHLIST_KEY) render();
    });

    // Re-render when item removed from this page
    document.addEventListener('wishlist:changed', function () {
      render();
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     INLINE SVG ICONS (used by wishlist page JS card builder)
  ═══════════════════════════════════════════════════════════════════════ */

  var ICON_HEART =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true">' +
    '<path d="M178,40c-20.65,0-38.73,8.88-50,23.89C116.73,48.88,98.65,40,78,40a62.07,62.07,0,0,0-62,62c0,70,103.79,126.66,108.21,129a8,8,0,0,0,7.58,0C136.21,228.66,240,172,240,102A62.07,62.07,0,0,0,178,40ZM128,214.8C109.74,204.16,32,155.69,32,102A46.06,46.06,0,0,1,78,56c19.45,0,35.78,10.36,42.6,27a8,8,0,0,0,14.8,0c6.82-16.67,23.15-27,42.6-27a46.06,46.06,0,0,1,46,46C224,155.61,146.24,204.15,128,214.8Z"/>' +
    '</svg>';

  var ICON_HEART_FILL =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true">' +
    '<path d="M240,102c0,70-103.79,126.66-108.21,129a8,8,0,0,1-7.58,0C119.79,228.66,16,172,16,102A62.07,62.07,0,0,1,78,40c20.65,0,38.73,8.88,50,23.89C139.27,48.88,157.35,40,178,40A62.07,62.07,0,0,1,240,102Z"/>' +
    '</svg>';

  var ICON_CART =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true">' +
    '<path d="M104,216a16,16,0,1,1-16-16A16,16,0,0,1,104,216Zm88-16a16,16,0,1,0,16,16A16,16,0,0,0,192,200ZM239.71,74.14l-25.64,92.28A24.06,24.06,0,0,1,191,184H92.16A24.06,24.06,0,0,1,69,166.42L33.92,40H16a8,8,0,0,1,0-16H40a8,8,0,0,1,7.71,5.86L57.19,64H232a8,8,0,0,1,7.71,10.14ZM221.47,80H61.64l22.81,82.14A8,8,0,0,0,92.16,168H191a8,8,0,0,0,7.71-5.86Z"/>' +
    '</svg>';

  var ICON_CHECK =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true">' +
    '<path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/>' +
    '</svg>';

  /* ═══════════════════════════════════════════════════════════════════════
     UTILITIES
  ═══════════════════════════════════════════════════════════════════════ */

  function formatMoney(cents) {
    return window.themeFormatMoney(cents);
  }

  function esc(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function escAttr(str) {
    return esc(str).replace(/"/g, '&quot;');
  }

  /* ═══════════════════════════════════════════════════════════════════════
     BOOT
  ═══════════════════════════════════════════════════════════════════════ */

  /* Whole-card click navigation (replaces the card's old inline onclick,
     which also fired for wishlist / quick-view button clicks and navigated
     away before their handlers could act). Links and buttons inside the
     card keep their own behavior. */
  function initCardNavigation() {
    document.addEventListener('click', function (e) {
      var card = e.target.closest('.product-card[data-product-url]');
      if (!card) return;
      if (e.target.closest('a, button')) return;
      window.location.href = card.getAttribute('data-product-url');
    });
  }

  function init() {
    initAddToCart();
    initWishlistToggle();
    syncWishlistButtons();
    initWishlistPage();
    initCardNavigation();

    // Re-sync heart states when a section swaps in fresh product cards
    // (collection.js dispatches this after each AJAX filter update)
    document.addEventListener('wishlist:sync', syncWishlistButtons);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
